import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import useAccountActionScope from '../hooks/useAccountActionScope';
import BackgroundPositionDisclosure from './BackgroundPositionDisclosure';
import BackgroundPositionSettings from './BackgroundPositionSettings';
import './DojoRequirementsModal.css';
import GracePeriodCountdown from './GracePeriodCountdown';
import ModalLoading from './ModalLoading';

const audienceName = audience => ({ dojo_chief: 'Dojo Chief', dojo: 'Dojo', friends: 'Friends', public: 'Public' }[audience] || audience);

const DojoRequirementsModal = ({ close, backendService, dojoId, dojoName, isChief = false, embedded = false, onRequirementsUpdated }) => {
  const captureAccount = useAccountActionScope(backendService, dojoId);
  const [isLoading, setIsLoading] = useState(true);
  const [requirements, setRequirements] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isFixingPrivacy, setIsFixingPrivacy] = useState(false);
  
  // Form state
  const [reqPerformanceEnabled, setReqPerformanceEnabled] = useState(false);
  const [reqPerformanceWindow, setReqPerformanceWindow] = useState('week');
  const [reqConsistencyEnabled, setReqConsistencyEnabled] = useState(false);
  const [reqConsistencyRate, setReqConsistencyRate] = useState(80);
  const [reqNutritionEnabled, setReqNutritionEnabled] = useState(false);
  const [reqNutritionRate, setReqNutritionRate] = useState(70);
  const [reqIntensityEnabled, setReqIntensityEnabled] = useState(false);
  const [reqIntensityRate, setReqIntensityRate] = useState(10);
  const [reqPrivacyWorkout, setReqPrivacyWorkout] = useState('');
  const [reqPrivacyMeal, setReqPrivacyMeal] = useState('');
  const [reqPrivacyMedia, setReqPrivacyMedia] = useState('');
  const [reqPrivacyMetric, setReqPrivacyMetric] = useState('');
  const [reqPrivacyPosition, setReqPrivacyPosition] = useState('');
  const [reqLocationCaptureEnabled, setReqLocationCaptureEnabled] = useState(false);
  const [reqBackgroundCapture, setReqBackgroundCapture] = useState(false);
  const [reqBackgroundAudience, setReqBackgroundAudience] = useState('');
  const [reviewBackground, setReviewBackground] = useState(false);

  const [reqMaxBodyFat, setReqMaxBodyFat] = useState('');
  const [reqMinMuscleIndex, setReqMinMuscleIndex] = useState('');
  const [reqGracePeriod, setReqGracePeriod] = useState(7);
  const [reqCoachDailyInsightChanges, setReqCoachDailyInsightChanges] = useState(false);
  const [reqCoachExerciseChanges, setReqCoachExerciseChanges] = useState(false);


  const loadRequirements = useCallback(async () => {
    if (!dojoId) return;
    const account = captureAccount();
    try {
      setIsLoading(true);
      const reqData = await backendService.getDojoRequirements(dojoId);
      if (!account.current()) return;
      if (reqData) {
        setRequirements(reqData.requirements);
        setCompliance(reqData.compliance);
      }
    } catch (error) {
      if (account.current()) console.error('Error loading requirements:', error);
    } finally {
      if (account.current()) setIsLoading(false);
    }
  }, [backendService, dojoId, captureAccount]);

  useLayoutEffect(() => {
    setRequirements(null); setCompliance(null); setReviewBackground(false); setShowEditForm(false);
  }, [captureAccount]);
  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  // Helper functions for compliance status display
  const getViolation = (type, metric) => {
    if (!compliance?.violations) return null;
    return compliance.violations.find(v => v.type === type && (v.metric === metric || v.activityType === metric));
  };

  // Get current stat value from either violation or currentStats
  const getCurrentValue = (type, metric) => {
    const violation = getViolation(type, metric);
    if (violation) return violation.current;
    
    // Get from currentStats
    const stats = compliance?.currentStats;
    if (!stats) return null;
    
    if (type === 'performance') {
      return stats.performance?.[metric];
    } else if (type === 'privacy') {
      return stats.privacy?.[metric];
    } else if (type === 'body_composition') {
      return stats.bodyComposition?.[metric];
    }
    return null;
  };

  // Check if current value is close to the limit (within 10% margin)
  const isCloseToLimit = (type, metric, required) => {
    const current = getCurrentValue(type, metric);
    if (current === null || current === undefined) return false;
    
    if (type === 'performance') {
      // For performance metrics, close means within 10% above the requirement
      const margin = required * 0.1;
      return current >= required && current < required + margin;
    } else if (type === 'body_composition') {
      if (metric === 'body_fat') {
        // For max body fat, close means within 2% below the limit
        return current <= required && current > required - 2;
      } else if (metric === 'muscle_index') {
        // For min muscle index, close means within 10% above the min
        const margin = required * 0.1;
        return current >= required && current < required + margin;
      }
    }
    return false;
  };

  // Check if we have no data for a required metric
  const hasNoData = (type, metric) => {
    const current = getCurrentValue(type, metric);
    if (type === 'privacy') return false; // Privacy always has a value
    return current === null || current === undefined;
  };

  const getComplianceClass = (type, metric, required) => {
    if (!compliance) return '';
    const violation = getViolation(type, metric);
    if (violation) return 'req-not-compliant';
    if (hasNoData(type, metric)) return 'req-not-compliant'; // No data = not compliant
    if (isCloseToLimit(type, metric, required)) return 'req-warning';
    return 'req-compliant';
  };

  const renderComplianceStatus = (type, metric, required) => {
    if (!compliance) return null;
    
    const violation = getViolation(type, metric);
    const current = getCurrentValue(type, metric);
    const closeToLimit = !violation && isCloseToLimit(type, metric, required);
    
    // Format display based on type
    let currentDisplay = '';
    let hasNoData = false;
    
    if (type === 'performance') {
      if (current !== null && current !== undefined) {
        currentDisplay = `${Math.round(current * 100)}%`;
      } else {
        currentDisplay = 'No data';
        hasNoData = true;
      }
    } else if (type === 'privacy') {
      // Privacy is now an array of audiences
      if (Array.isArray(current)) {
        if (current.length === 0) {
          currentDisplay = 'private';
        } else if (current.includes('public')) {
          currentDisplay = 'public';
        } else {
          currentDisplay = current.map(audienceName).join(', ');
        }
      } else {
        // Legacy string format
        currentDisplay = current ? audienceName(current) : 'private';
      }
    } else if (type === 'body_composition') {
      if (current !== null && current !== undefined) {
        currentDisplay = current.toFixed(1);
      } else {
        currentDisplay = 'No data';
        hasNoData = true;
      }
    }
    
    // No data should show as a failure  
    if (violation || hasNoData) {
      return (
        <span className="compliance-indicator not-compliant">
          ❌ You: {currentDisplay}
        </span>
      );
    }
    
    // Show warning if close to limit
    if (closeToLimit) {
      return (
        <span className="compliance-indicator warning">
          ⚠️ You: {currentDisplay}
        </span>
      );
    }
    
    // Compliant - show value
    return (
      <span className="compliance-indicator compliant">
        ✅ You: {currentDisplay}
      </span>
    );
  };

  const openEditForm = (source = requirements) => {
    setReqPerformanceEnabled(source?.performance?.enabled || false);
    setReqPerformanceWindow(source?.performance?.window || 'week');
    setReqConsistencyEnabled(source?.performance?.consistency?.enabled || false);
    setReqConsistencyRate(Math.round((source?.performance?.consistency?.minRate || 0.8) * 100));
    setReqNutritionEnabled(source?.performance?.nutrition?.enabled || false);
    setReqNutritionRate(Math.round((source?.performance?.nutrition?.minRate || 0.7) * 100));
    setReqIntensityEnabled(source?.performance?.intensity?.enabled || false);
    setReqIntensityRate(Math.round((source?.performance?.intensity?.minRate || 0.1) * 100));
    setReqPrivacyWorkout(source?.privacy?.workout || '');
    setReqPrivacyMeal(source?.privacy?.meal || '');
    setReqPrivacyMedia(source?.privacy?.media || '');
    setReqPrivacyMetric(source?.privacy?.metric || '');
    setReqPrivacyPosition(source?.privacy?.position || '');
    setReqLocationCaptureEnabled(source?.location_capture_enabled === true);
    setReqBackgroundCapture(source?.background_location_capture_enabled === true);
    setReqBackgroundAudience(source?.background_position_audience === 'dojo_chief' ? 'dojo_chief' : '');

    setReqMaxBodyFat(source?.bodyComposition?.maxBodyFat || '');
    setReqMinMuscleIndex(source?.bodyComposition?.minMuscleIndex || '');
    setReqGracePeriod(source?.gracePeriodDays || 7);
    setReqCoachDailyInsightChanges(source?.coachAutoAcceptance?.daily_insight_changes || false);
    setReqCoachExerciseChanges(source?.coachAutoAcceptance?.exercise_coach_changes || false);

    setShowEditForm(true);
  };

  useEffect(() => {
    if (embedded && isChief && requirements) openEditForm(requirements);
    // Only a canonical requirements reload resets the chief's local draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, isChief, requirements]);

  const handleSaveRequirements = async (e) => {
    e.preventDefault();
    const account = captureAccount();
    try {
      setIsLoading(true);
      const newRequirements = {
        performance: {
          enabled: reqPerformanceEnabled,
          window: reqPerformanceWindow,
          consistency: {
            enabled: reqConsistencyEnabled,
            minRate: reqConsistencyRate / 100
          },
          nutrition: {
            enabled: reqNutritionEnabled,
            minRate: reqNutritionRate / 100
          },
          intensity: {
            enabled: reqIntensityEnabled,
            minRate: reqIntensityRate / 100
          }
        },
        privacy: {
          position: reqPrivacyPosition || null,
          workout: reqPrivacyWorkout || null,
          meal: reqPrivacyMeal || null,
          media: reqPrivacyMedia || null,
          metric: reqPrivacyMetric || null,

        },
        location_capture_enabled: reqLocationCaptureEnabled,
        background_location_capture_enabled: reqBackgroundCapture,
        background_position_audience: reqBackgroundAudience || null,
        bodyComposition: {
          maxBodyFat: reqMaxBodyFat ? parseFloat(reqMaxBodyFat) : null,
          minMuscleIndex: reqMinMuscleIndex ? parseFloat(reqMinMuscleIndex) : null
        },
        coachAutoAcceptance: {
          daily_insight_changes: reqCoachDailyInsightChanges,
          exercise_coach_changes: reqCoachExerciseChanges
        },

        gracePeriodDays: parseInt(reqGracePeriod) || 7
      };
      account.assert();
      const savedRequirements = await backendService.updateDojoRequirements(dojoId, newRequirements);
      if (!account.current()) return;
      setRequirements(savedRequirements);
      if (!embedded) setShowEditForm(false);
      await loadRequirements();
      if (!account.current()) return;
      onRequirementsUpdated?.();
    } catch (error) {
      if (account.current()) alert(error.response?.data?.error || 'Failed to update requirements');
    } finally {
      setIsLoading(false);
    }
  };

  const hasAnyRequirements = requirements && (
    requirements.performance?.enabled ||
    requirements.privacy?.workout || requirements.privacy?.meal ||
    requirements.privacy?.media || requirements.privacy?.metric || requirements.privacy?.position || requirements.location_capture_enabled || requirements.background_location_capture_enabled || requirements.background_position_audience ||
    requirements.bodyComposition?.maxBodyFat || requirements.bodyComposition?.minMuscleIndex ||
    requirements.recoveryDays?.enabled || requirements.travelReplacements?.enabled || requirements.coachAutoAcceptance?.daily_insight_changes || requirements.coachAutoAcceptance?.exercise_coach_changes
  );

  // Check if there are privacy violations that can be fixed
  const privacyViolations = compliance?.violations?.filter(v => v.type === 'privacy' && v.activityType !== 'position' &&
    ['workout', 'meal', 'media', 'metric'].includes(v.activityType)) || [];
  const hasPrivacyViolations = !isChief && privacyViolations.length > 0;

  // Calculate what privacy changes are needed to fix violations
  const getPrivacyFixChanges = () => {
    if (!privacyViolations.length || !compliance?.currentStats?.privacy) return null;
    
    const changes = {};
    for (const v of privacyViolations) {
      const currentAudiences = compliance.currentStats.privacy[v.activityType] || [];
      const currentArray = Array.isArray(currentAudiences) ? currentAudiences : [];
      // Add the required audience to current settings
      changes[v.activityType] = [...new Set([...currentArray, v.required])];
    }
    return changes;
  };

  const handleFixPrivacy = async () => {
    const account = captureAccount();
    const changes = getPrivacyFixChanges();
    if (!changes) return;
    
    try {
      setIsFixingPrivacy(true);
      
      // Get current privacy settings and merge with required changes
      const currentSettings = await backendService.getPrivacySettings();
      if (!currentSettings) throw new Error('Could not read current sharing settings');
      const newSettings = changes;
      account.assert();
      
      await backendService.updatePrivacySettings(newSettings);
      
      // Reload requirements to refresh compliance status
      await loadRequirements();
      if (!account.current()) return;
      onRequirementsUpdated?.();
      alert('Privacy settings updated. Review your current dojo compliance above.');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update privacy settings');
    } finally {
      setIsFixingPrivacy(false);
    }
  };

  if (isLoading && !requirements) {
    return (
      <div className={`dojo-requirements-modal ${embedded ? 'embedded' : ''}`}>
        {!embedded && <div className="dojo-requirements-modal-header">
          <h2>📋 Membership Requirements</h2>
        </div>}
        <ModalLoading text="Loading requirements..." />
      </div>
    );
  }

  return (
    <div className={`dojo-requirements-modal ${embedded ? 'embedded' : ''}`}>
      {!embedded && <div className="dojo-requirements-modal-header">
        <h2>📋 {dojoName ? `${dojoName} ` : ''}Membership Requirements</h2>
      </div>}
      
      <div className="dojo-requirements-modal-body">
        {/* Chiefs see violations too, but are not subject to automatic removal. */}
        {compliance && (
          <div className={`overall-compliance-banner ${compliance.isCompliant || compliance.compliant ? 'compliant' : 'non-compliant'}`}>
            {compliance.isCompliant || compliance.compliant ? (
              <>✅ You are meeting all requirements</>
            ) : (
              compliance.autoRemovalEligible === true ?
                <>⚠️ You are not meeting all requirements - fix within <GracePeriodCountdown
                  firstViolationAt={compliance.firstViolationAt}
                  gracePeriodDays={requirements?.gracePeriodDays || 7}
                  expiredText="grace period expired!"
                /></> :
                <>⚠️ You are not meeting all requirements. Review your settings; this is an advisory warning, not an automatic removal notice.</>
            )}
          </div>
        )}

        {!showEditForm ? (
          <div className="requirements-display">
            {/* Performance Requirements */}
            {requirements?.performance?.enabled && (
              <div className="req-category">
                <h3>📊 Performance ({requirements.performance.window}ly)</h3>
                <ul>
                  {requirements.performance.consistency?.enabled && (
                    <li className={getComplianceClass('performance', 'consistency', requirements.performance.consistency.minRate)}>
                      <span className="req-label">Consistency: {Math.round(requirements.performance.consistency.minRate * 100)}%</span>
                      {renderComplianceStatus('performance', 'consistency', requirements.performance.consistency.minRate)}
                    </li>
                  )}
                  {requirements.performance.nutrition?.enabled && (
                    <li className={getComplianceClass('performance', 'nutrition', requirements.performance.nutrition.minRate)}>
                      <span className="req-label">Nutrition: {Math.round(requirements.performance.nutrition.minRate * 100)}%</span>
                      {renderComplianceStatus('performance', 'nutrition', requirements.performance.nutrition.minRate)}
                    </li>
                  )}
                  {requirements.performance.intensity?.enabled && (
                    <li className={getComplianceClass('performance', 'intensity', requirements.performance.intensity.minRate)}>
                      <span className="req-label">Intensity: {Math.round(requirements.performance.intensity.minRate * 100)}%</span>
                      {renderComplianceStatus('performance', 'intensity', requirements.performance.intensity.minRate)}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {(requirements?.location_capture_enabled || requirements?.privacy?.position) && (
              <div className="req-category">
                <h3>📍 Position requirements</h3>
                {requirements.location_capture_enabled && <p>Fresh device GPS position is required to complete each activity. If GPS permission is denied, the activity remains pending.</p>}
                {requirements.privacy?.position && <p>Set Position explicitly in Profile → Privacy; automatic fixes will not change it. {requirements.privacy.position === 'public' && 'Public shares your exact coordinates with anyone.'}</p>}
              </div>
            )}
            {(requirements?.background_location_capture_enabled || requirements?.background_position_audience) && <section className="background-position">
              <h3>Separate background position requirements</h3>
              <p>Background acquisition consent: {requirements.background_location_capture_enabled ? 'required' : 'not required'}. Trail audience: {requirements.background_position_audience === 'dojo_chief' ? 'Dojo Chief · exact coordinates' : 'Private allowed'}.</p>
              <p>Configuration-only compliance is separate from tracking health. GPS, permission, Doze, battery and offline failures are not removal violations. Rule changes do not enable consent, request permission or Start a collector. Stop and narrowing remain available.</p>
              <BackgroundPositionDisclosure />
              <button type="button" onClick={() => setReviewBackground(v => !v)}>Review background consent and sharing</button>
              {reviewBackground && <BackgroundPositionSettings backendService={backendService} />}
            </section>}
            {/* Privacy Requirements */}
            {(requirements?.privacy?.position || requirements?.privacy?.workout || requirements?.privacy?.meal ||
              requirements?.privacy?.media || requirements?.privacy?.metric) && (
              <div className="req-category">
                <h3>🔓 Privacy Requirements</h3>
                <p className="req-subtitle">Must include these audiences in your privacy settings</p>
                <ul>
                  {requirements.privacy.position && (
                    <li className={getComplianceClass('privacy', 'position', requirements.privacy.position)}>
                      <span className="req-label">Position: must include <strong>{audienceName(requirements.privacy.position)}</strong></span>
                      {renderComplianceStatus('privacy', 'position', requirements.privacy.position)}
                    </li>
                  )}
                  {requirements.privacy.workout && (
                    <li className={getComplianceClass('privacy', 'workout', requirements.privacy.workout)}>
                      <span className="req-label">Workouts: must include <strong>{requirements.privacy.workout}</strong></span>
                      {renderComplianceStatus('privacy', 'workout', requirements.privacy.workout)}
                    </li>
                  )}
                  {requirements.privacy.meal && (
                    <li className={getComplianceClass('privacy', 'meal', requirements.privacy.meal)}>
                      <span className="req-label">Meals: must include <strong>{requirements.privacy.meal}</strong></span>
                      {renderComplianceStatus('privacy', 'meal', requirements.privacy.meal)}
                    </li>
                  )}
                  {requirements.privacy.media && (
                    <li className={getComplianceClass('privacy', 'media', requirements.privacy.media)}>
                      <span className="req-label">Media: must include <strong>{requirements.privacy.media}</strong></span>
                      {renderComplianceStatus('privacy', 'media', requirements.privacy.media)}
                    </li>
                  )}
                  {requirements.privacy.metric && (
                    <li className={getComplianceClass('privacy', 'metric', requirements.privacy.metric)}>
                      <span className="req-label">Metrics: must include <strong>{requirements.privacy.metric}</strong></span>
                      {renderComplianceStatus('privacy', 'metric', requirements.privacy.metric)}
                    </li>
                  )}

                </ul>
                {/* Fix Privacy Button - shown when there are privacy violations */}
                {hasPrivacyViolations && (
                  <div className="fix-privacy-section">
                    <p className="fix-privacy-description">
                      Your current privacy settings don't meet the requirements. Click below to automatically fix them.
                    </p>
                    <button 
                      className="fix-privacy-btn"
                      onClick={handleFixPrivacy}
                      disabled={isFixingPrivacy}
                    >
                      {isFixingPrivacy ? '🔄 Fixing...' : '🔧 Fix My Privacy Settings'}
                    </button>
                    <p className="fix-privacy-hint">
                      This adds the required activity audiences only when you click. Dashboard charts follow Workouts, Meals and Metrics sharing independently; photos follow Media sharing. You can instead leave the dojo without granting access.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Body Composition Requirements */}
            {(requirements?.bodyComposition?.maxBodyFat || requirements?.bodyComposition?.minMuscleIndex) && (
              <div className="req-category">
                <h3>💪 Body Composition</h3>
                <ul>
                  {requirements.bodyComposition.maxBodyFat && (
                    <li className={getComplianceClass('body_composition', 'body_fat', requirements.bodyComposition.maxBodyFat)}>
                      <span className="req-label">Max Body Fat: {requirements.bodyComposition.maxBodyFat}%</span>
                      {renderComplianceStatus('body_composition', 'body_fat', requirements.bodyComposition.maxBodyFat)}
                    </li>
                  )}
                  {requirements.bodyComposition.minMuscleIndex && (
                    <li className={getComplianceClass('body_composition', 'muscle_index', requirements.bodyComposition.minMuscleIndex)}>
                      <span className="req-label">Min Muscle Index: {requirements.bodyComposition.minMuscleIndex}</span>
                      {renderComplianceStatus('body_composition', 'muscle_index', requirements.bodyComposition.minMuscleIndex)}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Coach Change Requirements */}

            {(requirements?.coachAutoAcceptance?.daily_insight_changes || requirements?.coachAutoAcceptance?.exercise_coach_changes) && (
              <div className="req-category">
                <h3>🤖 Coach Change Requirements</h3>
                <p className="req-subtitle">These settings must be enabled before joining.</p>
                <ul>
                  {requirements.coachAutoAcceptance.daily_insight_changes && <li>Daily insight changes: automatic acceptance required</li>}
                  {requirements.coachAutoAcceptance.exercise_coach_changes && <li>Exercise coach changes: automatic acceptance required</li>}
                </ul>
              </div>
            )}

            {/* Rest Days policy; balances live with Strategy & Plan. */}
            {requirements?.recoveryDays?.enabled && (
              <div className="req-category">
                <h3>🌴 Rest Days</h3>
                <p className="req-subtitle">
                  {requirements.recoveryDays.perYear} per year, established by the dojo chief.
                  See your personal balance and recovery in Dojo Settings → Strategy & Plan.
                </p>
              </div>
            )}

            {requirements && requirements.travelReplacements?.enabled && (
              <div className="req-category">
                <h3>🧳 Travel Replacements</h3>
                <p className="req-subtitle">
                  {requirements.travelReplacements.perYear} per year, established by the dojo chief.
                  Each replacement must be completed by the original workout deadline to preserve consistency credit.
                </p>
              </div>
            )}

            {/* No requirements message */}
            {!hasAnyRequirements && (
              <div className="no-requirements-message">
                <p>🎉 No requirements set - anyone can join and stay!</p>
              </div>
            )}

            {/* Grace Period */}
            {requirements?.gracePeriodDays && hasAnyRequirements && (
              <div className="grace-period-info">
                {compliance?.autoRemovalEligible === true && !(compliance.isCompliant || compliance.compliant) && compliance.firstViolationAt ? (
                  <span>⏳ <GracePeriodCountdown 
                    firstViolationAt={compliance.firstViolationAt}
                    gracePeriodDays={requirements.gracePeriodDays}
                    expiredText="Grace period expired!"
                  /> remaining to fix violations before removal</span>
                ) : (
                  compliance?.autoRemovalEligible === true ?
                    <span>⏳ Grace period: {requirements.gracePeriodDays} days to fix violations before removal</span> :
                    <span>Requirements review: no automatic removal applies to your role.</span>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Edit Form */
          <form className="requirements-edit-form" onSubmit={handleSaveRequirements}>
            <div className="req-edit-section">
              <h3>📊 Performance Requirements</h3>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={reqPerformanceEnabled}
                  onChange={(e) => setReqPerformanceEnabled(e.target.checked)}
                />
                Enable performance requirements
              </label>
              {reqPerformanceEnabled && (
                <div className="req-edit-options">
                  <div className="form-group">
                    <label>Time window:</label>
                    <select value={reqPerformanceWindow} onChange={(e) => setReqPerformanceWindow(e.target.value)}>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                    </select>
                  </div>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={reqConsistencyEnabled}
                      onChange={(e) => setReqConsistencyEnabled(e.target.checked)}
                    />
                    Minimum consistency rate
                  </label>
                  {reqConsistencyEnabled && (
                    <div className="form-group inline">
                      <input 
                        type="number" 
                        min="0" max="100" 
                        value={reqConsistencyRate}
                        onChange={(e) => setReqConsistencyRate(e.target.value)}
                      /> %
                    </div>
                  )}
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={reqNutritionEnabled}
                      onChange={(e) => setReqNutritionEnabled(e.target.checked)}
                    />
                    Minimum nutrition tracking rate
                  </label>
                  {reqNutritionEnabled && (
                    <div className="form-group inline">
                      <input 
                        type="number" 
                        min="0" max="100" 
                        value={reqNutritionRate}
                        onChange={(e) => setReqNutritionRate(e.target.value)}
                      /> %
                    </div>
                  )}
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={reqIntensityEnabled}
                      onChange={(e) => setReqIntensityEnabled(e.target.checked)}
                    />
                    Minimum intensity workouts
                  </label>
                  {reqIntensityEnabled && (
                    <div className="form-group inline">
                      <input 
                        type="number" 
                        min="0" max="100" 
                        value={reqIntensityRate}
                        onChange={(e) => setReqIntensityRate(e.target.value)}
                      /> %
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <section className="background-position">
              <h3>Independent background position requirements</h3>
              <label><input aria-label="Require background acquisition consent" type="checkbox" checked={reqBackgroundCapture} onChange={e => setReqBackgroundCapture(e.target.checked)} />Require background acquisition consent (configuration only)</label>
              <label>Background trail audience<select aria-label="Required background trail audience" value={reqBackgroundAudience} onChange={e => setReqBackgroundAudience(e.target.value)}><option value="">Private allowed</option><option value="dojo_chief">Require Dojo Chief · exact coordinates</option><option value="public" disabled>Public — unsupported</option><option value="friends" disabled>Friends — unsupported</option></select></label>
              <BackgroundPositionDisclosure />
              <p>Only Private or the currently authorized Dojo Chief (exact trail coordinates) is supported; public and friends cannot be required.</p>
              <p>Saving a rule never grants consent, requests permissions or Starts collection. Tracking interruptions are diagnostics, not membership-removal violations. Safety Stop and audience narrowing remain available.</p>
            </section>
            <div className="req-edit-section">
              <h3>🔓 Privacy Requirements</h3>
              <p className="req-hint">Require members to include specific audiences in their privacy settings</p>
              <label className="checkbox-label">
                <input type="checkbox" checked={reqLocationCaptureEnabled} onChange={e => setReqLocationCaptureEnabled(e.target.checked)} />
                Require position capture with fresh device GPS at activity completion
              </label>
              <div className="form-group">
                <label>Position:</label>
                <select aria-label="Required foreground Position audience" value={reqPrivacyPosition} onChange={e => setReqPrivacyPosition(e.target.value)}>
                  <option value="">No sharing requirement (private allowed)</option>
                  <option value="friends">Must include Friends 👥</option>
                  <option value="dojo">Must include Dojo 🥋</option>
                  <option value="dojo_chief">Must include Dojo Chief 👑</option>
                  <option value="public">Must be Public 🌍</option>
                </select>
                {reqPrivacyPosition === 'public' && <p role="alert">Warning: Public shares members' exact coordinates with anyone.</p>}
              </div>
              <div className="form-group">
                <label>Workouts:</label>
                <select value={reqPrivacyWorkout} onChange={(e) => setReqPrivacyWorkout(e.target.value)}>
                  <option value="">No requirement</option>
                  <option value="dojo">Must include Dojo 🥋</option>
                  <option value="dojo_chief">Must include Dojo Chief 👑</option>
                  <option value="public">Must be Public 🌍</option>
                </select>
              </div>
              <div className="form-group">
                <label>Meals:</label>
                <select value={reqPrivacyMeal} onChange={(e) => setReqPrivacyMeal(e.target.value)}>
                  <option value="">No requirement</option>
                  <option value="dojo">Must include Dojo 🥋</option>
                  <option value="dojo_chief">Must include Dojo Chief 👑</option>
                  <option value="public">Must be Public 🌍</option>
                </select>
              </div>
              <div className="form-group">
                <label>Media:</label>
                <select value={reqPrivacyMedia} onChange={(e) => setReqPrivacyMedia(e.target.value)}>
                  <option value="">No requirement</option>
                  <option value="dojo">Must include Dojo 🥋</option>
                  <option value="dojo_chief">Must include Dojo Chief 👑</option>
                  <option value="public">Must be Public 🌍</option>
                </select>
              </div>
              <div className="form-group">
                <label>Metrics:</label>
                <select value={reqPrivacyMetric} onChange={(e) => setReqPrivacyMetric(e.target.value)}>
                  <option value="">No requirement</option>
                  <option value="dojo">Must include Dojo 🥋</option>
                  <option value="dojo_chief">Must include Dojo Chief 👑</option>
                  <option value="public">Must be Public 🌍</option>
                </select>
              </div>

            </div>
            
            <div className="req-edit-section">
              <h3>💪 Body Composition</h3>
              <div className="form-group">
                <label>Max body fat %:</label>
                <input 
                  type="number" 
                  min="1" max="50" 
                  step="0.1"
                  value={reqMaxBodyFat}
                  onChange={(e) => setReqMaxBodyFat(e.target.value)}
                  placeholder="e.g., 25"
                />
              </div>
              <div className="form-group">
                <label>Min muscle index:</label>
                <input 
                  type="number" 
                  min="0" max="100" 
                  step="0.1"
                  value={reqMinMuscleIndex}
                  onChange={(e) => setReqMinMuscleIndex(e.target.value)}
                  placeholder="e.g., 30"
                />
              </div>
            </div>
            
            <div className="req-edit-section">
              <h3>🌴 Rest Days</h3>
              <p className="req-subtitle">
                The dojo allowance is managed in Dojo Settings → Strategy & Plan → Your Rest Days.
                Each member's remaining balance and recovery stay personal.
              </p>
            </div>

            <div className="req-edit-section">
              <h3>🧳 Travel Replacements</h3>
              <p className="req-subtitle">
                Managed in Dojo Settings → Strategy & Plan. This replaces only a member-owned, non-expired workout occurrence.
              </p>
            </div>
            
            <div className="req-edit-section">
              <h3>🤖 Coach Change Requirements</h3>
              <p className="req-hint">Members must accept these settings before joining this dojo.</p>
              <label className="checkbox-label"><input type="checkbox" checked={reqCoachDailyInsightChanges} onChange={(e) => setReqCoachDailyInsightChanges(e.target.checked)} /> Require automatic acceptance of daily insight changes</label>
              <label className="checkbox-label"><input type="checkbox" checked={reqCoachExerciseChanges} onChange={(e) => setReqCoachExerciseChanges(e.target.checked)} /> Require automatic acceptance of exercise coach changes</label>

            </div>

            <div className="req-edit-section">
              <h3>⏳ Grace Period</h3>
              <div className="form-group">
                <label>Days before removing non-compliant members:</label>
                <input 
                  type="number" 
                  min="1" max="90" 
                  value={reqGracePeriod}
                  onChange={(e) => setReqGracePeriod(e.target.value)}
                />
              </div>
            </div>
          </form>
        )}
      </div>

      {(!embedded || isChief) && <div className="dojo-requirements-modal-footer">
        {!showEditForm ? (
          <>
            {isChief && !embedded && (
              <button className="modal-btn primary" onClick={() => openEditForm()}>
                Edit Requirements
              </button>
            )}
            {!embedded && <button className="modal-btn" onClick={close}>
              Close
            </button>}
          </>
        ) : (
          <>
            <button className="modal-btn primary" onClick={handleSaveRequirements} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Requirements'}
            </button>
            {!embedded && <button className="modal-btn" onClick={() => setShowEditForm(false)}>
              Cancel
            </button>}
          </>
        )}
      </div>}
    </div>
  );
};

export default DojoRequirementsModal;
